import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './HomePage.css';
import '../components/KiloHubAnimation.css';
import Header from '../components/Header';
import EnhancedLoginModal from '../components/EnhancedLoginModal';
import TermsOfServiceModal from '../components/TermsOfServiceModal';
import DailyCheckinPage from './DailyCheckinPage';
import { BaseLayout, GamePopup, KiloHubAnimation } from '../components';
import { testUserProfileAPI } from '../utils/testAPI';
import { authApi, userApi } from '../services/api';
import { ReferralManager } from '../utils/referralManager';
import TermsManager from '../utils/termsManager';
import gamePopupService from '../services/gamePopupService';
import { API_CONFIG } from '../config/apiConfig';
import Top10Popup from '../components/Top10Popup';

// Image paths from the public folder
const backgroundImage = '/images/background.png';
const bstKilobaImage = '/images/bst_kiloba.png';
const dailyCheckInImage = '/images/daily_check_in.png';
const kiloHubImage = '/images/kilo_hub.png';
const dailyQuestImage = '/images/daily_quest.png';
const logo30NamImage = '/images/logo_kienlong_30th.png';
const miniGameImage = '/images/mini_game.png';
const stand = '/images/stand.png';

const HomePage: React.FC = () => {
  const { isAuthenticated, user, isLoading, appLogin, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showDailyCheckin, setShowDailyCheckin] = useState(false);
  const [appLoginLoading, setAppLoginLoading] = useState(false);
  const [appLoginError, setAppLoginError] = useState<string | null>(null);
  const [isAppLoginSuccess, setIsAppLoginSuccess] = useState(false);
  const [hasRefreshedOnMount, setHasRefreshedOnMount] = useState(false);
  const [animationFramesLoaded, setAnimationFramesLoaded] = useState(false);
  
  // App login terms of service states
  const [appLoginToken, setAppLoginToken] = useState<string | null>(null);
  const [showAppLoginTerms, setShowAppLoginTerms] = useState(false);
  
  // Pending Actions states
  const [isFirstLoginToday, setIsFirstLoginToday] = useState(false);
  const [hasFetchedPendingActions, setHasFetchedPendingActions] = useState(false);

  // Game Tour states
  const [isFirstTime, setIsFirstTime] = useState<boolean>(false);
  const [currentTourStep, setCurrentTourStep] = useState<number>(0);
  const [showTour, setShowTour] = useState<boolean>(false);
  
  // Top 10 popup state
  const [showTop10Popup, setShowTop10Popup] = useState(false);

  // Preload animation frames for smooth KILOBA animation
  useEffect(() => {
    const preloadFrames = async () => {
      try {
        const framePromises = [];
        // Create array of all frame URLs
        for (let i = 1; i <= 24; i++) {
          const frameNumber = i.toString().padStart(5, '0');
          const frameUrl = `/images/ani_kilohub/Animation KILOBA_${frameNumber}.png`;
          
          const promise = new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(frameUrl);
            img.onerror = () => {
              console.warn(`Failed to load animation frame: ${frameUrl}`);
              resolve(frameUrl); // Don't reject, just resolve to continue
            };
            img.src = frameUrl;
          });
          
          framePromises.push(promise);
        }
        
        // Wait for all frames to load (or fail)
        await Promise.all(framePromises);
        setAnimationFramesLoaded(true);
        console.log('KILOBA animation frames preloaded successfully');
      } catch (error) {
        console.error('Error preloading animation frames:', error);
        setAnimationFramesLoaded(true); // Set to true anyway to show animation
      }
    };

    preloadFrames();
  }, []);

  // Calculate modal state - don't show login modal if app login is in progress or successful
  const showLoginModal = !isLoading && !isAuthenticated && !appLoginLoading && !isAppLoginSuccess;
  const forceLogin = !isLoading && !isAuthenticated && !isAppLoginSuccess;

  // Tour steps configuration
  const tourSteps = [
    {
      className: 'central-hub',
      superTitle: 'KILOHUB',
      title: 'Chào mừng bạn đến với KILOHUB\n- Nơi hội tụ năng lượng!',
      description1: 'Nâng cấp Kiloba của bạn trở nên\ntoàn năng tại đây!',
      description2: 'Nhanh tay trở thành một trong những Kiloba Master\nđầu tiên chạm tay tới phần quà giá trị cực đã\ncủa KienlongBank'
    },
    {
      className: 'daily-checkin-area',
      superTitle: 'Điểm Danh Hàng Ngày',
      title: 'Đừng bỏ lỡ bất kỳ ngày nào đó',
      description1: 'Điểm danh mỗi ngày để nhận những\nphần quà giá trị từ KienlongBank. Chỉ những người chăm chỉ\nnhất sẽ là người chiến thắng',
      description2: 'Hãy đồng hành cùng Kiloba khám phá vũ trụ Kiloba mỗi ngày!'
    },
    {
      className: 'daily-quest-area',
      superTitle: 'Nhiệm Vụ Hàng Ngày',
      title: 'Bạn đã sẵn sàng cho những\nthử thách mỗi ngày?',
      description1: 'Hoàn thành chuỗi nhiệm vụ để chứng tỏ\nbản lĩnh và tích lũy năng lượng.',
      description2: 'Đây là con đường duy nhất để Kiloba\ncủa bạn thống trị Vũ trụ Kiloba!'
    },
    {
      className: 'mini-game-area',
      superTitle: 'Mini-Game',
      title: 'Nơi nhanh nhất để tích lũy sức mạnh!',
      description1: 'Xông pha vào các minigame đầy kịch tính,\nthể hiện kỹ năng của bạn và chiếm lấy nguồn\nnăng lượng vô tận.',
      description2: 'Chứng tỏ bản lĩnh, thống trị bảng xếp hạng Kiloba!'
    },
    {
      className: 'bst-kiloba-area',
      superTitle: 'Bộ Sưu Tập KILOBA',
      title: 'Mục tiêu cuối cùng?\nPhần thưởng cao nhất?',
      description1: 'Chỉ dành cho những người chơi xuất sắc nhất!',
      description2: 'Hãy nhanh tay thu thập trọn bộ Kiloba\nđể trở thành những người đầu tiên giành giải thưởng giá trị.'
    }
  ];

  // Check if first time user
  useEffect(() => {
    const hasVisited = localStorage.getItem('kiloba_has_visited');
    if (!hasVisited && isAuthenticated) {
      setIsFirstTime(true);
      setShowTour(true);
      localStorage.setItem('kiloba_has_visited', 'true');
    }
  }, [isAuthenticated]);

  // Handle tour navigation
  const nextTourStep = () => {
    if (currentTourStep < tourSteps.length - 1) {
      setCurrentTourStep(currentTourStep + 1);
    } else {
      setShowTour(false);
      setCurrentTourStep(0);
    }
  };

  const skipTour = () => {
    setShowTour(false);
    setCurrentTourStep(0);
  };

  // Manual tour restart function (for testing/debug)
  const restartTour = () => {
    setCurrentTourStep(0);
    setShowTour(true);
  };

  // Clear first-time flag (for testing)
  const clearFirstTimeFlag = () => {
    // localStorage.removeItem('kiloba_has_visited');
    console.log('First-time flag cleared. Refresh to see first-time experience.');
  };

  // Check and show pending actions popups
  const checkPendingActions = async () => {
    try {
      console.log('=== checkPendingActions called ===');
      console.log('isAuthenticated:', isAuthenticated);
      console.log('hasFetchedPendingActions:', hasFetchedPendingActions);
      
      if (!isAuthenticated || hasFetchedPendingActions) {
        console.log('Early return: not authenticated or already fetched');
        return;
      }

      console.log('Fetching pending actions...');
      const response = await userApi.getPendingActions();
      console.log('API response:', response);
      
      if (response.success && response.data) {
        const { energy, hasCheckedInToday } = response.data;
        console.log('Energy:', energy, 'HasCheckedInToday:', hasCheckedInToday);
        
        // Check if first login today
        const lastLoginDate = localStorage.getItem('kiloba_last_login_date');
        const today = new Date().toDateString();
        const isFirstLoginTodayCheck = lastLoginDate !== today;
        
        
        // Update last login date
        localStorage.setItem('kiloba_last_login_date', today);
        setIsFirstLoginToday(isFirstLoginTodayCheck);
        
        // Create popup queue based on conditions
        const popups = [];
        
        // 1. Top 10 announcement for Nov 13 - ONLY on first login of the day
        const currentDate = new Date();
        const isNov13 = currentDate.getMonth() === 10 && currentDate.getDate() === 13; // Month is 0-indexed
        
        if (isFirstLoginTodayCheck && isNov13) {
          popups.push({
            type: 'top10' as const,
            title: 'CỰC HOT',
            message: 'Top 10 Kiloba Master tháng 10 đã chính thức lộ diện! Tên bạn có ghi danh trên bảng vàng? Truy cập BST Kiloba và khám phá ngay nhé!',
            actionText: 'Bảng vàng',
            onAction: () => {
              // Close current popup and show Top 10 popup
              gamePopupService.dismissCurrentPopup();
              setShowTop10Popup(true);
            },
          });
        }
        
        // 2. First login today popup (only if not Nov 13, otherwise it's replaced by Top 10 announcement)
        if (isFirstLoginTodayCheck && !isNov13) {
          popups.push({
            type: 'welcome' as const,
            title: 'Xin chào!',
            message: `Hôm nay Kiloba có 3 mini-game đang chờ bạn khám phá đó nha 👋`,
          });
        }
        
        // 3. Daily check-in reminder
        if (!hasCheckedInToday) {
          popups.push({
            type: 'checkin' as const,
            title: 'Đừng quên điểm danh!',
            message: 'Hãy điểm danh ngay để nhận thêm năng lượng và vật phẩm quý giá.',
            actionText: 'Điểm danh',
            onAction: () => setShowDailyCheckin(true),
          });
        }
        
        // 4. Energy notification (only if user has energy)
        if (energy > 0) {
          popups.push({
            type: 'energy' as const,
            message: `Bạn đang có ${energy} năng lượng, vào Kiloba Hub thăng cấp ngay thôi nào`,
            actionText: 'Vào Kiloba Hub',
            onAction: () => navigate('/kiloba-hub'),
            duration: 10000 // 10 seconds for energy
          });
        }
        
        // Show popups if any
        
        if (popups.length > 0) {
          gamePopupService.showPopups(popups);
        } else {
          console.log('No popups to show');
        }
        
        setHasFetchedPendingActions(true);
      } else {
        console.log('API call failed or no data:', response);
      }
    } catch (error) {
      console.error('Error fetching pending actions:', error);
      setHasFetchedPendingActions(true); // Mark as attempted to avoid retry loops
    }
  };

  // Add highlight class to current tour step element
  useEffect(() => {
    if (showTour) {
      // Remove all existing highlights
      document.querySelectorAll('.tour-highlight').forEach(el => {
        el.classList.remove('tour-highlight');
      });
      
      // Add highlight to current step
      const currentElement = document.querySelector(`.${tourSteps[currentTourStep].className}`);
      if (currentElement) {
        currentElement.classList.add('tour-highlight');
      }
    } else {
      // Remove all highlights when tour ends
      document.querySelectorAll('.tour-highlight').forEach(el => {
        el.classList.remove('tour-highlight');
      });
    }
    
    // Cleanup function
    return () => {
      document.querySelectorAll('.tour-highlight').forEach(el => {
        el.classList.remove('tour-highlight');
      });
    };
  }, [showTour, currentTourStep]);



  // Handle refBy parameter from URL
  useEffect(() => {
    const refBy = ReferralManager.extractRefByFromUrl(searchParams);
    if (refBy) {
      console.log('Found refBy in URL:', refBy);
      ReferralManager.setRefBy(refBy);
      
      // Remove refBy from URL after saving to localStorage
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('refBy');
      const newUrl = window.location.pathname + (newSearchParams.toString() ? '?' + newSearchParams.toString() : '');
      window.history.replaceState({}, '', newUrl);
    }
  }, [searchParams]);

  // Refresh user data only once when user becomes authenticated and component is ready
  useEffect(() => {
    if (isAuthenticated && !isLoading && !appLoginLoading && !hasRefreshedOnMount) {
      refreshUser()
        .then(() => {
          setHasRefreshedOnMount(true);
        })
        .catch(error => {
          setHasRefreshedOnMount(true); // Still mark as attempted to avoid retry loops
        });
    }
  }, [isAuthenticated, isLoading, appLoginLoading, hasRefreshedOnMount, refreshUser]);

  // Check pending actions after user is fully authenticated and refreshed
  useEffect(() => {
    console.log('=== useEffect for pending actions ===');
    console.log('isAuthenticated:', isAuthenticated);
    console.log('hasRefreshedOnMount:', hasRefreshedOnMount);
    console.log('isLoading:', isLoading);
    console.log('appLoginLoading:', appLoginLoading);
    console.log('showTour:', showTour);
    console.log('hasFetchedPendingActions:', hasFetchedPendingActions);
    
    if (isAuthenticated && hasRefreshedOnMount && !isLoading && !appLoginLoading && !showTour && !hasFetchedPendingActions) {
      console.log('All conditions met, setting timer...');
      // Small delay to ensure all auth processes are complete
      const timer = setTimeout(() => {
        console.log('Timer fired, calling checkPendingActions...');
        checkPendingActions();
      }, 1000);
      
      return () => {
        console.log('Clearing timer...');
        clearTimeout(timer);
      };
    } else {
      console.log('Conditions not met, not calling checkPendingActions');
    }
  }, [isAuthenticated, hasRefreshedOnMount, isLoading, appLoginLoading, showTour]);

  // Handle app login from URL token
  useEffect(() => {
    const handleAppLogin = async () => {
      try {
        // Get token directly from URL without any automatic decoding
        const urlString = window.location.href;
        const tokenMatch = urlString.match(/[?&]token=([^&]*)/);
        
        if (!tokenMatch || !tokenMatch[1]) {
          return; // No token, continue normal flow
        }

        const rawToken = tokenMatch[1];
        setAppLoginLoading(true);

        // Manually decode the token
        const decodedToken = decodeURIComponent(rawToken);

        // Check if user has already accepted terms
        if (TermsManager.hasAcceptedTerms()) {
          // User has accepted terms, proceed directly with login
          console.log('User has already accepted terms, proceeding with app login');
          
          const success = await appLogin(decodedToken);
          
          if (success) {
            setIsAppLoginSuccess(true);
            
            // Remove token from URL after successful login
            const newUrl = window.location.pathname;
            window.history.replaceState({}, '', newUrl);
          } else {
            setAppLoginError('Đăng nhập thất bại');
          }
          
          setAppLoginLoading(false);
        } else {
          // User hasn't accepted terms, show terms modal first
          console.log('User needs to accept terms, showing terms modal');
          setAppLoginToken(decodedToken);
          setShowAppLoginTerms(true);
          setAppLoginLoading(false);
        }
        
      } catch (error) {
        setAppLoginError('Lỗi xử lý token đăng nhập');
        setAppLoginLoading(false);
      }
    };

    // Only run app login if not already authenticated
    if (!isAuthenticated && !isLoading) {
      handleAppLogin();
    }
  }, [isAuthenticated, isLoading]); // Remove searchParams dependency

  // Reset app login success state when user is authenticated
  useEffect(() => {
    if (isAuthenticated && isAppLoginSuccess) {
      setIsAppLoginSuccess(false);
    }
  }, [isAuthenticated, isAppLoginSuccess]);

  // Handle app login terms acceptance
  const handleAppLoginTermsAccept = async () => {
    if (!appLoginToken) {
      setAppLoginError('Token đăng nhập không hợp lệ');
      return;
    }

    // Save terms acceptance to localStorage BEFORE proceeding with login
    TermsManager.acceptTerms();
    console.log('Terms accepted and saved for app login');

    setAppLoginLoading(true);
    setShowAppLoginTerms(false);

    try {
      // Call app login through AuthContext
      const success = await appLogin(appLoginToken);
      
      if (success) {
        // Mark app login as successful to prevent showing login modal
        setIsAppLoginSuccess(true);
        
        // Remove token from URL after successful login
        const newUrl = window.location.pathname;
        window.history.replaceState({}, '', newUrl);
        
        // Clear the stored token
        setAppLoginToken(null);
        
        // The AuthContext will handle the authentication state update
        // and the page will re-render automatically
      } else {
        setAppLoginError('Đăng nhập thất bại');
      }
    } catch (error) {
      setAppLoginError('Lỗi kết nối hoặc token không hợp lệ');
    } finally {
      setAppLoginLoading(false);
    }
  };

  // Handle app login terms decline
  const handleAppLoginTermsDecline = () => {
    setShowAppLoginTerms(false);
    setAppLoginToken(null);
    
    // Remove token from URL
    const newUrl = window.location.pathname;
    window.history.replaceState({}, '', newUrl);
    
    // Show error message
    setAppLoginError('Bạn cần đồng ý với điều khoản sử dụng để tiếp tục');
  };

  // Add test API to window for development
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).testUserProfileAPI = testUserProfileAPI;
      (window as any).restartTour = restartTour; // Add tour restart for testing
      (window as any).clearFirstTimeFlag = clearFirstTimeFlag; // Add clear flag for testing
      
      // Add popup testing functions
      
      
      (window as any).testPendingActions = () => {
        console.log('Testing pending actions...');
        setHasFetchedPendingActions(false); // Reset flag
        checkPendingActions();
      };
      
      (window as any).resetPendingActions = () => {
        console.log('Resetting pending actions flag...');
        setHasFetchedPendingActions(false);
        localStorage.removeItem('kiloba_last_login_date'); // Reset login date too
      };

      // Terms management debug functions
      (window as any).checkTermsAcceptance = () => {
        const details = TermsManager.getAcceptanceDetails();
        console.log('Terms Acceptance Status:', details);
        return details;
      };

      (window as any).clearTermsAcceptance = () => {
        console.log('Clearing terms acceptance...');
        TermsManager.clearTermsAcceptance();
        console.log('Terms acceptance cleared');
      };

      (window as any).acceptTerms = () => {
        console.log('Manually accepting terms...');
        TermsManager.acceptTerms();
        console.log('Terms accepted');
      };
      
      (window as any).fullReset = () => {
        console.log('Full reset - clearing localStorage and reloading...');
        localStorage.removeItem('kiloba_last_login_date');
        localStorage.removeItem('kiloba_has_visited');
        TermsManager.clearTermsAcceptance(); // Also clear terms acceptance
        location.reload();
      };
      
      (window as any).testTop10Popup = () => {
        console.log('Testing Top 10 announcement popup...');
        gamePopupService.showPopup({
          type: 'top10' as const,
          title: 'CỰC HOT',
          message: 'Top 10 Kiloba Master tháng 10 đã chính thức lộ diện! Tên bạn có ghi danh trên bảng vàng? Truy cập BST Kiloba và khám phá ngay nhé!',
          actionText: 'Bảng vàng',
          onAction: () => {
            gamePopupService.dismissCurrentPopup();
            setShowTop10Popup(true);
          },
        });
      };
      
      (window as any).forceTestPopups = () => {
        console.log('Force testing popups with fake data...');
        
        // Simulate the popup logic with fake data
        const energy = 0;
        const hasCheckedInToday = false;
        const lastLoginDate = localStorage.getItem('kiloba_last_login_date');
        const today = new Date().toDateString();
        const isFirstLoginTodayCheck = lastLoginDate !== today;
        
        console.log('Simulating conditions:');
        console.log('- Energy:', energy);
        console.log('- hasCheckedInToday:', hasCheckedInToday);
        console.log('- isFirstLoginToday:', isFirstLoginTodayCheck);
        
        const popups = [];
        
        if (isFirstLoginTodayCheck) {
          console.log('Adding welcome popup');
          popups.push({
            type: 'welcome' as const,
            message: 'Xin chào, hôm nay Kiloba có 3 mini-game đang chờ bạn khám phá đó nha 👋',
            actionText: 'Khám phá ngay',
            onAction: () => navigate('/mini-game')
          });
        }
        
        if (!hasCheckedInToday) {
          console.log('Adding checkin popup');
          popups.push({
            type: 'checkin' as const,
            message: 'Đừng phá "chuỗi"! Điểm danh hàng ngày, nhận ngay năng lượng.',
            actionText: 'Điểm danh ngay',
            onAction: () => setShowDailyCheckin(true)
          });
        }
        
        if (energy > 0) {
          console.log('Adding energy popup');
          popups.push({
            type: 'energy' as const,
            message: `Bạn đang có ${energy} năng lượng, vào Kiloba Hub thăng cấp ngay thôi nào`,
            actionText: 'Vào Kiloba Hub',
            onAction: () => navigate('/kiloba-hub')
          });
        }
        
        console.log('Total popups to show:', popups.length);
        
        if (popups.length > 0) {
          console.log('Calling gamePopupService.showPopups...');
          gamePopupService.showPopups(popups);
        } else {
          console.log('No popups to show');
        }
      };
    }
  }, []);

  const handleDailyQuest = () => {
    if (!isAuthenticated || showTour) {
      return; // Modal đã hiển thị sẵn hoặc đang trong tour, không cần làm gì
    }
    navigate('/tasks');
  };

  const handleDailyCheckIn = () => {
    if (!isAuthenticated || showTour) {
      return; // Modal đã hiển thị sẵn hoặc đang trong tour, không cần làm gì
    }
    setShowDailyCheckin(true);
  };

  const handleMiniGame = () => {
    if (!isAuthenticated || showTour) {
      return; // Modal đã hiển thị sẵn hoặc đang trong tour, không cần làm gì
    }
    navigate('/mini-game');
  };

  const handleBSTKiloba = () => {
    if (!isAuthenticated || showTour) {
      return; // Modal đã hiển thị sẵn hoặc đang trong tour, không cần làm gì
    }
    navigate('/kiloba-collection');
  };

  const handleKiloHub = () => {
    if (!isAuthenticated || showTour) {
      return; // Modal đã hiển thị sẵn hoặc đang trong tour, không cần làm gì
    }
    navigate('/kiloba-hub');
  };

  const handleBack = () => {
    console.log('Back navigation');
  };

  const handleInfo = () => {
    console.log('Show information');
  };

  const handleNotification = () => {
    if (!isAuthenticated || showTour) {
      return; // Modal đã hiển thị sẵn hoặc đang trong tour, không cần làm gì
    }
    navigate('/notifications');
  };

  const handleSettings = () => {
    if (!isAuthenticated || showTour) {
      return; // Modal đã hiển thị sẵn hoặc đang trong tour, không cần làm gì
    }
    navigate('/settings');
  };

  const handleTop10 = () => {
    if (!isAuthenticated || showTour) {
      return; // Modal đã hiển thị sẵn hoặc đang trong tour, không cần làm gì
    }
    setShowTop10Popup(true);
  };

  const handleQRScan = () => {
    if (!isAuthenticated || showTour) {
      return; // Modal đã hiển thị sẵn hoặc đang trong tour, không cần làm gì
    }
    navigate('/event-qr-scan');
  };

  // User data for header - sử dụng dữ liệu thực tế từ authentication
  const userData = {
    name: user?.name || "Guest",
    coins: user?.energy || 0, // Sử dụng energy thực tế từ user data
    avatar: user?.avatarUrl ? `${API_CONFIG.BASE_URL.replace('/api', '')}${user.avatarUrl}` : undefined // Sử dụng avatarUrl từ API
  };

  const getBgImage = (currentTourStep: number) => {
    if (currentTourStep === 0) {
      return '/images/des_kilo_hub.png';
    }
    if (currentTourStep === 1) {
      return '/images/des_daily_quest.png';
    }
    if (currentTourStep === 2) {
      return '/images/des_daily_checkin.png';
    }

    if (currentTourStep === 3) {
      return '/images/des_mini_game.png';
    }

    return '/images/des_bst_kilo.png';
  }

  return (
    <div className={`viewport-wrapper ${showTour ? 'tour-active' : ''}`}>
      <BaseLayout className="homepage-container">
      {/* Loading indicator khi đang check authentication hoặc app login */}
      {(isLoading || appLoginLoading) && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999,
          color: 'white',
          fontSize: '18px'
        }}>
          {appLoginLoading ? 'Đang xử lý đăng nhập từ app...' : 'Đang kiểm tra đăng nhập...'}
        </div>
      )}

      {/* App login error indicator */}
      {appLoginError && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999,
          color: 'white',
          fontSize: '18px',
          flexDirection: 'column',
          padding: '20px'
        }}>
          <div style={{ marginBottom: '20px', fontSize: '24px' }}>❌</div>
          <div style={{ marginBottom: '10px', fontWeight: 'bold' }}>Đăng nhập thất bại</div>
          <div style={{ marginBottom: '20px', textAlign: 'center' }}>{appLoginError}</div>
          <button
            onClick={() => {
              setAppLoginError(null);
              // Remove token from URL
              const newUrl = window.location.pathname;
              window.history.replaceState({}, '', newUrl);
            }}
            style={{
              padding: '10px 20px',
              backgroundColor: '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            Đóng
          </button>
        </div>
      )}

      <div className="logo-section">
          <img src={logo30NamImage} alt="KienlongBank 30 năm" className="logo-30nam" />
        </div>

      {/* Header and Logo Section */}
      <div className="header-logo-wrapper safe-area-top">
        {/* Header Section - Fixed top */}
        <div className="header" style={{ paddingLeft: '20px', paddingRight: 20 }}>
          <Header
            title="VŨ TRỤ KILOBA"
            user={userData}
            onNotificationClick={handleNotification}
            onSettingsClick={handleSettings}
            onQRScanClick={handleQRScan}
            onTop10Click={handleTop10}
          />
        </div>

        {/* Logo 30 năm */}
        
      </div>

      {/* Main 3D Scene */}
      <main 
        className="main-scene" 
        style={{ 
          backgroundImage: `url(${backgroundImage})`,
          pointerEvents: showTour ? 'none' : 'auto' // Disable interactions during tour
        }}
      >
        <div className="scene-container">
          
          {/* Central KILO HUB with Robot Animation */}
          <div className="central-hub">
            <KiloHubAnimation 
              isLoaded={animationFramesLoaded} 
              onClick={handleKiloHub}
              className="kilohub-main-animation"
            />
          </div>

          <img src={stand} className='stand-image' alt="KILO HUB" />

          {/* Daily Quest - Top Left */}
          <div className="game-area daily-quest-area" onClick={handleDailyQuest}>
            <div className="area-content">
              <div className="area-icon-daily-quest">
                {/* Animation handled by CSS */}
              </div>
            </div>
          </div>

          {/* Daily Check In - Top Right */}
          <div className="game-area daily-checkin-area" onClick={handleDailyCheckIn}>
            <div className="area-content">
              <div className="area-icon-daily-checkin">
                {/* Animation handled by CSS */}
              </div>
            </div>
          </div>

          {/* Mini Game - Bottom Left */}
          <div className="game-area mini-game-area" onClick={handleMiniGame}>
            <div className="area-content">
              <div className="area-icon-mini-game">
                {/* Animation handled by CSS */}
              </div>
            </div>
          </div>

          {/* BST KILOBA - Bottom Right */}
          <div className="game-area bst-kiloba-area" onClick={handleBSTKiloba}>
            <div className="area-content">
              <div className="area-icon-bst-kiloba">
                {/* Animation handled by CSS */}
              </div>
            </div>
          </div>

          {/* Decorative Elements */}
          <div className="floating-elements">
            <div className="float-cube cube-1"></div>
            <div className="float-cube cube-2"></div>
            <div className="float-cube cube-3"></div>
            <div className="float-pyramid pyramid-1"></div>
            <div className="float-pyramid pyramid-2"></div>
          </div>

          {/* Roads/Paths */}
          <div className="roads">
            <div className="road road-horizontal"></div>
            <div className="road road-vertical"></div>
            <div className="road road-diagonal-1"></div>
            <div className="road road-diagonal-2"></div>
          </div>

        </div>
      </main>
      {/* Enhanced Login Modal */}
      <EnhancedLoginModal 
        isOpen={showLoginModal} 
        onClose={() => {
          // Modal will automatically close when authentication state changes
          // No need to manually manage modal state
        }}
        forceLogin={forceLogin}
      />

      {/* App Login Terms of Service Modal */}
      <TermsOfServiceModal
        isOpen={showAppLoginTerms}
        onClose={() => {
          // Don't allow closing by clicking outside during app login
          // User must explicitly accept or decline
        }}
        onAccept={handleAppLoginTermsAccept}
        onDecline={handleAppLoginTermsDecline}
        forceAction={true}
      />

      {/* Daily Checkin Modal */}
      {showDailyCheckin && (
        <DailyCheckinPage onClose={() => setShowDailyCheckin(false)} />
      )}

      {/* Game Tour Overlay */}
      {showTour && (
        <div className="tour-overlay" onClick={(e) => e.stopPropagation()} style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          zIndex: 9998,
          pointerEvents: 'auto'
        }}>
          <div className='tour-super-title' style={{
            position: 'absolute',
            top: currentTourStep === 0 ? '22%' : // central-hub
                  currentTourStep === 1 ? '15%' : // daily-quest-area (top left)
                  currentTourStep === 2 ? '15%' : // daily-checkin-area (top right)
                  currentTourStep === 3 ? '10%' : // mini-game-area (bottom left)
                  '10%', // bst-kiloba-area (bottom right)
            left: '50%',
            maxWidth: 500,
          }}>
            <span>{tourSteps[currentTourStep].superTitle}</span>
          </div>
          <div className="tour-container" style={{
              position: 'absolute',
              bottom: currentTourStep === 0 ? '0%' : // central-hub
                  currentTourStep === 1 ? '0%' : // daily-quest-area (top left)
                  currentTourStep === 2 ? '0%' :  // daily-checkin-area (top right)
                  undefined,

                  // nếu là step 0, 1, 2 thì để bottom 0%, còn 3,4 thì để top 0%
              top: currentTourStep >= 3 ? '20%' : undefined,
              left: '50%',
              transform: 'translate(-50%, 0)',
              maxWidth: '360px',
              width: '90%'
          }}>
            <div className="tour-content">
              <img style={{width: '90%'}} src={getBgImage(currentTourStep)}></img>
            </div>
            <div className="tour-controls" style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '15px',
              width: '90%',
              marginTop: '20px',
            }}>
              <button
                onClick={skipTour}
                style={{
                  backgroundColor: 'transparent',
                  color: '#000',
                  border: '2px solid #ffffff',
                  borderRadius: '24px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  height: '48px',
                  flex: 1,
                  background: 'rgba(255, 255, 255, 1)',
                }}
              >
                Bỏ qua
              </button>
              <button
                onClick={nextTourStep}
                className='btn-next-tour'
              >
                {currentTourStep < tourSteps.length - 1 ? 'Tiếp theo' : 'Hoàn thành'}
              </button>
            </div>
          </div>
          {/* Tour Content */}
          
        </div>
      )}

      {/* Game Popup System */}
      <GamePopup />
      
      {/* Top 10 Winners Popup */}
      <Top10Popup 
        isOpen={showTop10Popup} 
        onClose={() => setShowTop10Popup(false)} 
      />
      </BaseLayout>
    </div>
  );
};

export default HomePage;
